import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, ErrorMessage } from 'formik';
import { string, object } from 'yup';

import {
  Panel,
  PanelBody,
  H3,
  Label,
  Input,
  Icon,
  Tooltip,
  ErrorMsg,
  PanelHeader,
  PanelTitle,
} from '@studio/legacy-components';
import { selectPin, update } from 'next/entities/pins';

import { InformativeLabelText, DetailsForm } from './styled';

export function Details({ name, previewUrl, onSave }) {
  const previewUrlError = 'Must be a valid URL. This change will not be saved.';

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <H3>Details</H3>
        </PanelTitle>
      </PanelHeader>
      <PanelBody>
        <Formik
          // certain publishing actions (e.g. revert) trigger external prop changes
          // which should reinitialize the form...
          enableReinitialize
          validateOnBlur
          validationSchema={object().shape({
            name: string().required(
              'A Pin Experience name is required. This change will not be saved.'
            ),
            previewUrl: string()
              .urlWithLocalhost(previewUrlError)
              .required(previewUrlError)
              .typeError(previewUrlError),
          })}
          initialValues={{
            name,
            previewUrl,
          }}
        >
          {({ errors, handleChange, values, handleBlur, touched }) => {
            const handleNameSubmit = e => {
              if (name !== values.name && !errors.name) {
                onSave({ name: values.name });
              }
              handleBlur(e);
            };

            const handleUrlSubmit = e => {
              if (!errors.previewUrl) {
                onSave({ previewUrl: values.previewUrl });
              }
              handleBlur(e);
            };

            return (
              <DetailsForm>
                <Label htmlFor="name">
                  Pin Experience name
                  <Input
                    onChange={handleChange}
                    onBlur={handleNameSubmit}
                    value={values.name}
                    type="text"
                    id="name"
                    placeholder="Pin name"
                    aria-label="Pin Experience name"
                    error={Boolean(touched.name && errors.name)}
                  />
                  <ErrorMessage name="name" component={ErrorMsg} />
                </Label>

                <Label htmlFor="previewUrl">
                  <InformativeLabelText>
                    Build URL{' '}
                    <Tooltip
                      size="m"
                      wrapped
                      label='The Chrome extension will load at this URL when you edit the Pin Experience. You can set URL targeting criteria in the "Page targeting" section. The Build URL will not affect the behavior of published Pin Experiences.'
                    >
                      <Icon icon="info-circle" />
                    </Tooltip>
                  </InformativeLabelText>
                  <Input
                    onChange={handleChange}
                    onBlur={handleUrlSubmit}
                    value={values.previewUrl}
                    type="url"
                    id="previewUrl"
                    placeholder="http://myapp.com"
                    aria-label="Build URL"
                    error={Boolean(touched.previewUrl && errors.previewUrl)}
                  />
                  <ErrorMessage name="previewUrl" component={ErrorMsg} />
                </Label>
              </DetailsForm>
            );
          }}
        </Formik>
      </PanelBody>
    </Panel>
  );
}

Details.propTypes = {
  onSave: PropTypes.func,
  name: PropTypes.string,
  previewUrl: PropTypes.string,
};

const mapDispatchToProps = (dispatch, { id }) => ({
  onSave: delta => dispatch(update(id, delta)),
});

const mapStateToProps = (state, { id }) => {
  const pin = selectPin(state, id);

  return {
    name: pin.name,
    previewUrl: pin.previewUrl,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
